import axios, { AxiosResponse } from 'axios';
import configFile from '../../../config.json';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ControllerType } from '../inputform/types';
import { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  bgcolor: 'background.paper',
  border: '2px solid #eee',
  boxShadow: 24,
  p: 4,
};

const parseNumberString = (input: string): number[] | null => {
  if (!input) return null;

  if (!isNaN(Number(input))) {
    return [Number(input)];
  }

  let delimiter = '';
  for (let i = 0; i < input.length; i++) {
    if (isNaN(Number(input[i])) && input[i] !== ' ' && input[i] !== '.') {
      delimiter = input[i];
      break;
    }
  }

  if (!delimiter) {
    delimiter = ' ';
  }

  const numberStrings = input
    .split(delimiter)
    .filter((str) => str.trim() !== '');
  return numberStrings.map((numStr) => Number(numStr.trim()));
};

export const MultiForm = ({ modelName }: { modelName: string }) => {
  const [controllers, setControllers] = useState<ControllerType[]>([]);

  const [open, setOpen] = useState(false);
  const [currentController, setCurrentController] =
    useState<ControllerType | null>(null);

  const [showSubcontroller, setShowSubcontroller] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [showControllerEquip, setShowControllerEquip] = useState(false);
  const [showAffiliatedEquip, setShowAffiliatedEquip] = useState(true);
  const [showEmissionRates, setShowEmissionRates] = useState(false);
  const [showNormEmissionRates, setShowNormEmissionRates] = useState(false);
  const [showProbability, setShowProbability] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsResultReceived] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [outputFile, setOutputFile] = useState<{
    name: string;
    href: string;
  } | null>(null);
  // const [outputTableData, setOutputTableData] = useState<ExcelRow[]>([]);
  const [config, setConfig] = useState<any>(null);

  const initialState = {
    pneumatic_type: '',
    model: 'Method 1',
    // cont_aff_equip: '',
    num_controllers: 0,
    emission_rate: '',
    normal_emission_rate: '',
    abnormal_emission_rate: '',
    probability_malfunc: '',
    probability_repair: '',
    associated_equip: 'Unknown',
  };
  const [formData, setFormData] = useState(initialState);

  const initialGlobalState = {
    stage_of_supply_chain: '',
    mc_iterations: '',
    time_scale: '',
    time_resolution: '',
  };
  const [globalData, setGlobalData] = useState(initialGlobalState);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setCurrentController(null);
    setFormData(initialState);
  };

  const addDefaults = (data: ControllerType) => {
    let controllerType;
    switch (data.pneumatic_type) {
      case 'Low-bleed':
        controllerType = 'LBC';
        break;
      case 'High-bleed':
        controllerType = 'HBC';
        break;
      case 'Intermittent-bleed':
        controllerType = 'IBC';
        break;
      default:
        controllerType = '';
        break;
    }

    const stage =
      globalData.stage_of_supply_chain &&
      globalData.stage_of_supply_chain === 'Production'
        ? 'production'
        : 'gathering_and_boosting';

    let method = data.model.toLowerCase().replaceAll(' ', '');

    if (controllerType === 'IBC' && data.associated_equip === 'High') {
      controllerType = 'HA_IBC';
      method = 'method3';
    }

    if (controllerType === 'IBC' && data.associated_equip === 'Low') {
      controllerType = 'LA_IBC';
      method = 'method3';
    }

    if (!data.abnormal_emission_rate) {
      data.abnormal_emission_rate = config.emissions[method]
        ? config.emissions[method][stage][controllerType + '_malfunctioning'] ||
          null
        : null;
    }

    if (!data.emission_rate && data.model === 'Method 1') {
      data.emission_rate = config.emissions[method]
        ? config.emissions[method][stage][controllerType + '_working'] || null
        : null;
    }

    if (!data.normal_emission_rate && data.model === 'Method 2') {
      data.normal_emission_rate = config.emissions[method]
        ? config.emissions[method][stage][controllerType + '_working'] || null
        : null;
    }

    if (!data.probability_repair && data.model === 'Method 2') {
      data.probability_repair = config.initial_probabilities[method]
        ? config.initial_probabilities[method][stage][controllerType] || null
        : null;

      if (data.probability_repair) {
        data.probability_repair = (
          parseFloat(data.probability_repair) * 100
        ).toString();
      }
    }

    if (!data.probability_malfunc && data.model === 'Method 2') {
      data.probability_malfunc = config.probabilities[method]
        ? config.probabilities[method][stage][controllerType] || null
        : null;

      if (data.probability_malfunc) {
        data.probability_malfunc = (
          parseFloat(data.probability_malfunc) * 100
        ).toString();
      }
    }

    return data;
  };

  const handleAddController = () => {
    const updatedFormdata = addDefaults(formData);
    const newController = {
      id: controllers.length + 1,
      ...updatedFormdata,
    };
    setControllers([...controllers, newController]);
    handleClose();
  };

  const handleEditController = () => {
    if (currentController) {
      setControllers(
        controllers.map((controller) =>
          controller.id === currentController.id
            ? { ...currentController, ...formData }
            : controller,
        ),
      );
      handleClose();
    }
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      associated_equip: e.target.value,
    });
  };

  const handleDeleteController = (id: number) => {
    setControllers(controllers.filter((controller) => controller.id !== id));
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleGlobalChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target;
    setGlobalData({
      ...globalData,
      [name as string]: value,
    });
  };

  const handleEditClick = (item: any) => {
    setCurrentController(item);
    setFormData({
      pneumatic_type: item.pneumatic_type,
      model: item.model,
      // cont_aff_equip: item.cont_aff_equip,
      num_controllers: item.num_controllers,
      emission_rate: item.emission_rate,
      normal_emission_rate: item.normal_emission_rate,
      abnormal_emission_rate: item.abnormal_emission_rate,
      probability_repair: item.probability_repair,
      probability_malfunc: item.probability_malfunc,
      associated_equip: item.associated_equip,
    });
    handleOpen();
  };

  const generateOutputFile = (response: { data: { file_data: string } }) => {
    const binaryString = response.data.file_data
      .split('')
      .map((char: string) => char.charCodeAt(0));
    const byteArray = new Uint8Array(binaryString);

    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return window.URL.createObjectURL(blob);
  };

  const proceedResponse = (response: AxiosResponse<any, any>) => {
    setIsLoading(false);
    setIsResultReceived(true);

    const url = generateOutputFile(response);

    setOutputFile({
      name: 'output-file.xlsx',
      href: url,
    });
  };

  const handleSubmit = () => {
    const LBC =
      controllers.filter((item) => item.pneumatic_type === 'Low-bleed')[0] ||
      null;
    const HBC =
      controllers.filter((item) => item.pneumatic_type === 'High-bleed')[0] ||
      null;
    const IBC =
      controllers.filter(
        (item) => item.pneumatic_type === 'Intermittent-bleed',
      )[0] || null;

    const LA_IBC =
      controllers.filter((item) => item.associated_equip === 'Low')[0] || null;

    const HA_IBC =
      controllers.filter((item) => item.associated_equip === 'High')[0] || null;

    const PUMP =
      controllers.filter((item) => item.pneumatic_type === 'Pneumatic')[0] ||
      null;
    const simulation_method: { [p: string]: string } = {};
    if (LBC) simulation_method['LBC'] = LBC.model.toLowerCase() || 'method 1';
    if (HBC) simulation_method['HBC'] = HBC.model.toLowerCase() || 'method 1';
    if (IBC) simulation_method['IBC'] = IBC.model.toLowerCase() || 'method 1';

    const request = {
      stage_of_supply_chain: globalData.stage_of_supply_chain,
      simulation_method,
      montecarlo_iterations:
        globalData.mc_iterations || config?.DEFAULT?.MONTE_CARLO_ITERATIONS,
      z_value: 0.95,
      simulation_time:
        globalData.time_scale || config?.DEFAULT?.SIMULATION_TIME,
      time_resolution:
        globalData.time_resolution || config?.DEFAULT?.TIME_RESOLUTION,
      pneumatic_controllers: {
        LBC: LBC ? +LBC.num_controllers : 0,
        HBC: HBC ? +HBC.num_controllers : 0,
        IBC:
          IBC && IBC.associated_equip === 'Unknown' ? +IBC.num_controllers : 0,
        LA_IBC: LA_IBC ? +LA_IBC.num_controllers : 0,
        HA_IBC: HA_IBC ? +HA_IBC.num_controllers : 0,
        PUMP: PUMP ? +PUMP.num_controllers : 0,
      },
      pneumatic_controller_emissions: {
        LBC: {
          malfunctioning: LBC
            ? LBC.model === 'Method 2'
              ? parseNumberString(LBC.abnormal_emission_rate)
              : null
            : null,
          working: LBC
            ? parseNumberString(
                LBC.model === 'Method 2'
                  ? LBC.normal_emission_rate
                  : LBC.emission_rate,
              )
            : null,
        },
        HBC: {
          malfunctioning: null,
          working: HBC ? parseNumberString(HBC.emission_rate) : null,
        },
        IBC: {
          malfunctioning:
            IBC && IBC.associated_equip === 'Unknown'
              ? IBC.model === 'Method 2'
                ? parseNumberString(IBC.abnormal_emission_rate)
                : null
              : null,
          working:
            IBC && IBC.associated_equip === 'Unknown'
              ? parseNumberString(
                  IBC.model === 'Method 2'
                    ? IBC.normal_emission_rate
                    : IBC.emission_rate,
                )
              : null,
        },
        LA_IBC: {
          malfunctioning:
            LA_IBC && IBC.associated_equip === 'Low'
              ? LA_IBC.model === 'Method 2'
                ? parseNumberString(LA_IBC.abnormal_emission_rate)
                : null
              : null,
          working:
            LA_IBC && IBC.associated_equip === 'Low'
              ? parseNumberString(
                  LA_IBC.model === 'Method 2'
                    ? LA_IBC.normal_emission_rate
                    : LA_IBC.emission_rate,
                )
              : null,
        },
        HA_IBC: {
          malfunctioning:
            HA_IBC && IBC.associated_equip === 'High'
              ? HA_IBC.model === 'Method 2'
                ? parseNumberString(HA_IBC.abnormal_emission_rate)
                : null
              : null,
          working:
            HA_IBC && IBC.associated_equip === 'High'
              ? parseNumberString(
                  HA_IBC.model === 'Method 2'
                    ? HA_IBC.normal_emission_rate
                    : HA_IBC.emission_rate,
                )
              : null,
        },
        PUMP: {
          malfunctioning: PUMP
            ? PUMP.model === 'Method 2'
              ? parseNumberString(PUMP.abnormal_emission_rate)
              : null
            : null,
          working: PUMP
            ? parseNumberString(
                PUMP.model === 'Method 2'
                  ? PUMP.normal_emission_rate
                  : PUMP.emission_rate,
              )
            : null,
        },
      },
      pneumatic_controller_initial_probabilities: {
        LBC:
          LBC && LBC.probability_repair ? +LBC.probability_repair / 100 : null,
        IBC:
          IBC && IBC.associated_equip === 'Unknown' && IBC.probability_repair
            ? +IBC.probability_repair / 100
            : null,
        LA_IBC:
          LA_IBC &&
          LA_IBC.associated_equip === 'Low' &&
          LA_IBC.probability_repair
            ? +LA_IBC.probability_repair / 100
            : null,
        HA_IBC:
          HA_IBC &&
          HA_IBC.associated_equip === 'High' &&
          HA_IBC.probability_repair
            ? +HA_IBC.probability_repair / 100
            : null,
      },
      pneumatic_controller_probabilities: {
        LBC:
          LBC && LBC.probability_malfunc
            ? +LBC.probability_malfunc / 100
            : null,
        IBC:
          IBC && IBC.associated_equip === 'Unknown' && IBC.probability_malfunc
            ? +IBC.probability_malfunc / 100
            : null,
        LA_IBC:
          LA_IBC &&
          LA_IBC.associated_equip === 'Low' &&
          LA_IBC.probability_malfunc
            ? +LA_IBC.probability_malfunc / 100
            : null,
        HA_IBC:
          HA_IBC &&
          HA_IBC.associated_equip === 'High' &&
          HA_IBC.probability_malfunc
            ? +HA_IBC.probability_malfunc / 100
            : null,
      },
    };

    setIsLoading(true);
    setOutputFile(null);
    setErrorMessage('');

    axios
      .post('https://flexplanfunc.azurewebsites.net/api/pneumatic', request, {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'DELETE, POST, GET, OPTIONS',
          'x-functions-key':
            'rZvvLSReEW5qQIuocv5iAxQxJIYCh9sPKqs6g5ivnrElAzFuY7RygA==',
        },
        responseEncoding: 'latin1',
      })
      .then((response) => {
        proceedResponse(response);
      })
      .catch((error) => {
        console.error('Error:', error);
        setIsLoading(false);
        setIsResultReceived(false);
        setErrorMessage(error.message);
      });
  };

  useEffect(() => {
    setConfig(configFile);
  }, []);

  useEffect(() => {
    setFormData({
      ...initialState,
      ...{ pneumatic_type: formData.pneumatic_type },
    });
  }, [formData.pneumatic_type]);

  useEffect(() => {
    setShowSubcontroller(
      formData.pneumatic_type === 'Intermittent-bleed' &&
        formData.model === 'Method 2',
    );
    setShowModel(
      formData.pneumatic_type === 'Intermittent-bleed' ||
        formData.pneumatic_type === 'Low-bleed',
    );
    setShowControllerEquip(
      formData.pneumatic_type === 'Intermittent-bleed' &&
        formData.model === 'Method 2',
    );
    setShowAffiliatedEquip(
      formData.associated_equip === '' ||
        formData.associated_equip === 'Unknown',
    );
    setShowEmissionRates(formData.model === 'Method 1');
    setShowNormEmissionRates(formData.model === 'Method 2');
    setShowProbability(formData.model === 'Method 2');
  }, [formData]);

  useEffect(() => {
    if (currentController) {
      setFormData({
        pneumatic_type: currentController.pneumatic_type || '',
        model: currentController.model || '',
        // cont_aff_equip: currentController.cont_aff_equip || '',
        num_controllers: currentController.num_controllers || 0,
        emission_rate: currentController.emission_rate || '',
        normal_emission_rate: currentController.normal_emission_rate || '',
        abnormal_emission_rate: currentController.abnormal_emission_rate || '',
        probability_repair: currentController.probability_repair || '',
        probability_malfunc: currentController.probability_malfunc || '',
        associated_equip: currentController.associated_equip || 'Unknown',
      });
    }
  }, [currentController]);

  return (
    <form /* onSubmit={formik.handleSubmit}*/>
      <Grid
        container
        spacing={3}
        padding={3}
        alignItems={'top'}
        justifyItems={'center'}
      >
        <Grid
          container
          spacing={3}
          padding={3}
          alignItems={'top'}
          justifyItems={'center'}
          alignContent={'flex-start'}
          xs={12}
          /*sm={isResultReceived ? 3 : 9}*/
        >
          <Grid item container xs={12} sm={12}>
            <Grid item xs={3} sm={3} paddingRight={3}>
              <Divider>
                <h4>Global parameters</h4>
              </Divider>
              <>
                <Grid container>
                  <Grid item xs={11} sm={11} paddingRight={2}>
                    <FormControl fullWidth>
                      <InputLabel>Supply Chain Segment</InputLabel>
                      <Select
                        name="stage_of_supply_chain"
                        value={globalData.stage_of_supply_chain}
                        onChange={handleGlobalChange}
                      >
                        <MenuItem value="Production">Production</MenuItem>
                        <MenuItem value="Boosting">
                          Boosting &amp; Gathering
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1} sm={1} paddingTop={2}>
                    <Tooltip
                      title={
                        <>
                          <h3>Supply chain segment description</h3>
                        </>
                      }
                      arrow
                      placement={'top'}
                    >
                      <InfoIcon color="primary" />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={11} sm={11} paddingRight={2} paddingTop={2}>
                    <TextField
                      name={'mc_iterations'}
                      label={'Monte Carlo iterations'}
                      value={
                        globalData.mc_iterations ||
                        config?.DEFAULT?.MONTE_CARLO_ITERATIONS ||
                        ''
                      }
                      onChange={handleGlobalChange}
                      // error={
                      //   formik.touched[entry[0] as keyof ValuesType] &&
                      //   Boolean(formik.errors[entry[0] as keyof ValuesType])
                      // }
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} paddingTop={4}>
                    <Tooltip
                      title={
                        <>
                          <h3>Number of Monte Carlo iterations</h3>
                        </>
                      }
                      arrow
                      placement={'top'}
                    >
                      <InfoIcon color="primary" />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={11} sm={11} paddingRight={2} paddingTop={2}>
                    <TextField
                      name={'time_scale'}
                      label={'Simulation time scale'}
                      value={
                        globalData.time_scale ||
                        config?.DEFAULT?.SIMULATION_TIME ||
                        ''
                      }
                      onChange={handleGlobalChange}
                      // error={
                      //   formik.touched[entry[0] as keyof ValuesType] &&
                      //   Boolean(formik.errors[entry[0] as keyof ValuesType])
                      // }
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} paddingTop={4}>
                    <Tooltip
                      title={
                        <>
                          <h3>Time scale of simulations (in hours)</h3>
                        </>
                      }
                      arrow
                      placement={'top'}
                    >
                      <InfoIcon color="primary" />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={11} sm={11} paddingRight={2} paddingTop={2}>
                    <TextField
                      name={'time_resolution'}
                      label={'Simulation time resolution'}
                      value={
                        globalData.time_resolution ||
                        config?.DEFAULT?.TIME_RESOLUTION ||
                        ''
                      }
                      onChange={handleGlobalChange}
                      // error={
                      //   formik.touched[entry[0] as keyof ValuesType] &&
                      //   Boolean(formik.errors[entry[0] as keyof ValuesType])
                      // }
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} paddingTop={4}>
                    <Tooltip
                      title={
                        <>
                          <h3>Duration of simulation (in hours)</h3>
                        </>
                      }
                      arrow
                      placement={'top'}
                    >
                      <InfoIcon color="primary" />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid>
                    <Button
                      disabled={isLoading}
                      variant={'outlined'}
                      onClick={handleSubmit}
                      sx={{ marginTop: '16px' }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Run Model'}
                    </Button>
                    {errorMessage && (
                      <Alert severity="error" sx={{ marginTop: '10px' }}>
                        {errorMessage}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </>
            </Grid>

            <Grid item xs={9} sm={9}>
              <Divider>
                <h4>Controller parameters</h4>
              </Divider>
              <>
                <Grid container>
                  {controllers.length > 0 && (
                    <div className="table-container withwrap">
                      <table>
                        <thead>
                          <tr>
                            <td>#</td>
                            <td>Pneumatic type</td>
                            <td>Method</td>
                            {/*<td>Controller Affiliated Equipment</td>*/}
                            <td>Controller Type</td>
                            <td>Number of controllers</td>
                            <td>Emission sample rate(s)</td>
                            <td>Normal Emission sample rate(s)</td>
                            <td>Abnormal Emission sample rate(s)</td>
                            <td>
                              Percentage of properly operating at start: (%):
                            </td>
                            <td>Probability of state change (%):</td>
                            <td></td>
                          </tr>
                        </thead>
                        <tbody>
                          {controllers.length > 0 &&
                            controllers.map((item: ControllerType) => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{item.pneumatic_type}</td>
                                <td>{item.model}</td>
                                {/*<td>{item.cont_aff_equip}</td>*/}
                                <td>{item.associated_equip || 'N/A'}</td>
                                <td>{item.num_controllers || 'N/A'}</td>
                                <td>
                                  {item.emission_rate &&
                                    item.emission_rate.length <= 5 && (
                                      <>{item.emission_rate}</>
                                    )}
                                  {item.emission_rate &&
                                    item.emission_rate.length > 5 && (
                                      <Tooltip
                                        title={<>{item.emission_rate}</>}
                                        arrow
                                        placement={'left'}
                                        sx={{ marginLeft: '16px' }}
                                      >
                                        <p>
                                          {item.emission_rate &&
                                            item.emission_rate.slice(0, 20)}
                                        </p>
                                      </Tooltip>
                                    )}
                                  {!item.emission_rate && <>N/A</>}
                                </td>
                                <td>
                                  {item.normal_emission_rate &&
                                    item.normal_emission_rate.length <= 5 && (
                                      <>{item.normal_emission_rate}</>
                                    )}
                                  {item.normal_emission_rate &&
                                    item.normal_emission_rate.length > 5 && (
                                      <Tooltip
                                        title={<>{item.normal_emission_rate}</>}
                                        arrow
                                        placement={'left'}
                                        sx={{ marginLeft: '16px' }}
                                      >
                                        <p>
                                          {item.normal_emission_rate &&
                                            item.normal_emission_rate.slice(
                                              0,
                                              20,
                                            )}
                                        </p>
                                      </Tooltip>
                                    )}
                                  {!item.normal_emission_rate && <>N/A</>}
                                </td>
                                <td>
                                  {item.abnormal_emission_rate &&
                                    item.abnormal_emission_rate.length <= 5 && (
                                      <>{item.abnormal_emission_rate}</>
                                    )}
                                  {item.abnormal_emission_rate &&
                                    item.abnormal_emission_rate.length > 5 && (
                                      <Tooltip
                                        title={
                                          <>{item.abnormal_emission_rate}</>
                                        }
                                        arrow
                                        placement={'left'}
                                        sx={{ marginLeft: '16px' }}
                                      >
                                        <p>
                                          {item.abnormal_emission_rate &&
                                            item.abnormal_emission_rate.slice(
                                              0,
                                              20,
                                            )}
                                        </p>
                                      </Tooltip>
                                    )}
                                  {!item.abnormal_emission_rate && <>N/A</>}
                                </td>
                                <td>{item.probability_repair || 'N/A'}</td>
                                <td>{item.probability_malfunc || 'N/A'}</td>
                                <td>
                                  <Button
                                    variant={'outlined'}
                                    onClick={() => handleEditClick(item)}
                                  >
                                    <EditIcon />
                                  </Button>

                                  <Button
                                    variant={'outlined'}
                                    onClick={() =>
                                      item.id && handleDeleteController(item.id)
                                    }
                                  >
                                    <DeleteIcon />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Grid>
                <Grid container>
                  <Button
                    onClick={handleOpen}
                    variant={'contained'}
                    disabled={
                      controllers.length >= 3 ||
                      !globalData.stage_of_supply_chain
                    }
                  >
                    Add New Controller
                  </Button>
                  <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box sx={style}>
                      <Grid container>
                        <Grid item xs={12} sm={12}>
                          <Typography
                            id="modal-modal-title"
                            variant="h5"
                            component="h3"
                            align={'center'}
                            marginBottom={1}
                          >
                            {currentController
                              ? 'Edit Controller'
                              : 'Add New Controller'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                          <Grid container spacing={2} padding={2}>
                            <Grid item xs={12} sm={12}>
                              <FormControl fullWidth>
                                <InputLabel>Pneumatic type</InputLabel>
                                <Select
                                  name="pneumatic_type"
                                  value={formData.pneumatic_type}
                                  onChange={handleChange}
                                >
                                  <MenuItem value="Low-bleed">
                                    Low-bleed controller
                                  </MenuItem>
                                  <MenuItem value="High-bleed">
                                    High-bleed controller
                                  </MenuItem>
                                  <MenuItem value="Intermittent-bleed">
                                    Intermittent-bleed controller
                                  </MenuItem>
                                  <MenuItem value="Pneumatic">
                                    Pneumatic pump
                                  </MenuItem>
                                </Select>
                              </FormControl>
                              {showSubcontroller && (
                                <FormControl sx={{ marginTop: '16px' }}>
                                  <FormLabel sx={{ color: 'inherit' }}>
                                    Controller Type
                                  </FormLabel>
                                  <RadioGroup
                                    row
                                    name="associated_equip"
                                    value={formData.associated_equip}
                                    onChange={handleRadioChange}
                                  >
                                    <FormControlLabel
                                      value="Unknown"
                                      control={<Radio />}
                                      label="Unknown"
                                    />
                                    <FormControlLabel
                                      value="Low"
                                      control={<Radio />}
                                      label="Low Actuation"
                                    />
                                    <FormControlLabel
                                      value="High"
                                      control={<Radio />}
                                      label="High Actuation"
                                    />
                                  </RadioGroup>
                                </FormControl>
                              )}
                            </Grid>
                            <Grid item xs={11} sm={11}>
                              <FormControl
                                fullWidth
                                disabled={!showModel}
                                className={!showModel ? 'input-disabled' : ''}
                              >
                                <InputLabel>Method</InputLabel>
                                <Select
                                  name="model"
                                  value={
                                    !showModel ? 'Method 1' : formData.model
                                  }
                                  onChange={handleChange}
                                >
                                  <MenuItem value="Method 1">Method 1</MenuItem>
                                  <MenuItem value="Method 2">Method 2</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} paddingTop={2}>
                              <Tooltip
                                title={
                                  <>
                                    <h3>Method 1 and Method 2 description</h3>
                                  </>
                                }
                                arrow
                                placement={'top'}
                                sx={{ marginTop: '16px' }}
                              >
                                <InfoIcon color="primary" />
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <TextField
                                name="num_controllers"
                                label="Number of controllers"
                                value={formData.num_controllers}
                                onChange={handleChange}
                                type={'number'}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                          <Grid container spacing={2} padding={2}>
                            <Grid item xs={12} sm={12} sx={{ display: 'none' }}>
                              <FormControl
                                fullWidth
                                disabled={
                                  !showControllerEquip || !showAffiliatedEquip
                                }
                                className={
                                  !showControllerEquip || !showAffiliatedEquip
                                    ? 'input-disabled'
                                    : ''
                                }
                              >
                                <InputLabel>
                                  Controller Affiliated Equipment Type
                                </InputLabel>
                                {/*<Select*/}
                                {/*  name="cont_aff_equip"*/}
                                {/*  value={*/}
                                {/*    !showControllerEquip || !showAffiliatedEquip*/}
                                {/*      ? ''*/}
                                {/*      : formData.cont_aff_equip*/}
                                {/*  }*/}
                                {/*  onChange={handleChange}*/}
                                {/*>*/}
                                {/*  <MenuItem value="Tank">Tank</MenuItem>*/}
                                {/*  <MenuItem value="Separator">*/}
                                {/*    Separator*/}
                                {/*  </MenuItem>*/}
                                {/*</Select>*/}
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <TextField
                                name="emission_rate"
                                label="Emission sample rate(s)"
                                value={
                                  !showEmissionRates
                                    ? ''
                                    : formData.emission_rate
                                }
                                onChange={handleChange}
                                fullWidth
                                disabled={!showEmissionRates}
                                className={
                                  !showEmissionRates ? 'input-disabled' : ''
                                }
                              />
                            </Grid>

                            <>
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  name="normal_emission_rate"
                                  label="Normal Emission sample rate(s)"
                                  value={
                                    !showNormEmissionRates
                                      ? ''
                                      : formData.normal_emission_rate
                                  }
                                  onChange={handleChange}
                                  fullWidth
                                  disabled={!showNormEmissionRates}
                                  className={
                                    !showNormEmissionRates
                                      ? 'input-disabled'
                                      : ''
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  name="abnormal_emission_rate"
                                  label="Abnormal Emission sample rate(s)"
                                  value={formData.abnormal_emission_rate}
                                  onChange={handleChange}
                                  fullWidth
                                  disabled={!showNormEmissionRates}
                                  className={
                                    !showNormEmissionRates
                                      ? 'input-disabled'
                                      : ''
                                  }
                                />
                              </Grid>
                            </>

                            <>
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  name="probability_repair"
                                  label="Percentage of properly operating at start: (%):"
                                  value={
                                    !showProbability
                                      ? ''
                                      : formData.probability_repair
                                  }
                                  onChange={handleChange}
                                  fullWidth
                                  disabled={!showProbability}
                                  className={
                                    !showProbability ? 'input-disabled' : ''
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  name="probability_malfunc"
                                  label="Probability of state change (%):"
                                  value={
                                    !showProbability
                                      ? ''
                                      : formData.probability_malfunc
                                  }
                                  onChange={handleChange}
                                  fullWidth
                                  disabled={!showProbability}
                                  className={
                                    !showProbability ? 'input-disabled' : ''
                                  }
                                />
                              </Grid>
                            </>
                          </Grid>
                        </Grid>
                        <Grid item paddingTop={2} sx={{ marginTop: '-75px' }}>
                          <Button
                            fullWidth={true}
                            variant="contained"
                            onClick={
                              currentController
                                ? handleEditController
                                : handleAddController
                            }
                          >
                            Save Controller
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Modal>
                </Grid>
              </>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          padding={3}
          alignItems={'top'}
          justifyItems={'center'}
          alignContent={'flex-start'}
          xs={12}
          /*sm={isResultReceived ? 3 : 9}*/
        >
          <Grid item xs={12} sm={12}>
            <Divider>
              <h3>Output parameters</h3>
            </Divider>
            <>
              {outputFile && (
                <p>
                  <b>Output report:</b>{' '}
                  <a href={outputFile.href}>{outputFile.name}</a>
                </p>
              )}
            </>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};
